import { isTrellisTheme, useTheme } from 'foundations-theming/utils';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BannerContainer = styled.div.withConfig({
  displayName: "Banner__BannerContainer",
  componentId: "sc-6enntz-0"
})(({
  isTrellis,
  theme,
  hasActions
}) => css(["width:100%;height:40px;box-sizing:border-box;overflow:hidden;padding:", " !important;background-color:", ";display:grid;grid-template-columns:1fr auto;grid-template-rows:1fr;align-items:center;& > *:nth-child(1){grid-column:1 / -1;grid-row:1;justify-self:center;}& > *:nth-child(2){grid-column:2;grid-row:1;justify-self:end;}", ""], `${theme.space['200']} ${theme.space['300']}`, isTrellis ? theme.color['specialty-table-header-default'] : theme.color['fill-tertiary-default'], hasActions && `
      /* Switch to left-aligned title for smaller screens */
      @media screen and (max-width: 1080px) {
        & > *:nth-child(1) {
          grid-column: 1;
          justify-self: start;
        }
      }

      /* Switch to taller banner and column flex for actions when space gets tight */
      @media screen and (max-width: 650px) {
        height: 62px;
        & > *:nth-child(2) {
          flex-direction: column-reverse;
          align-items: flex-end;
          gap: ${theme.space['200']};
        }
      }
    `));
const PrimaryTitleContainer = styled.div.withConfig({
  displayName: "Banner__PrimaryTitleContainer",
  componentId: "sc-6enntz-1"
})(["display:flex;justify-content:center;min-width:0;"]);
const CTAContainer = styled.div.withConfig({
  displayName: "Banner__CTAContainer",
  componentId: "sc-6enntz-2"
})(({
  theme
}) => css(["display:flex;gap:", ";align-items:center;min-width:0;"], theme.space['300']));
const PrimaryTitle = styled.span.withConfig({
  displayName: "Banner__PrimaryTitle",
  componentId: "sc-6enntz-3"
})(["", " color:", ";"], ({
  theme
}) => theme.typography['heading-25'], ({
  theme
}) => theme.color['text-core-default']);
/**
 * Banner component that renders a centered title text and an actions container
 * on the right. Uses CSS Grid to keep the title perfectly centered in the full
 * container width until space gets constrained at 1080px, then left aligns the
 * title. When space gets constrained further at 650px, the actions container
 * is switched to a column-reverse flex.
 * @param id - The string id of the banner
 * @param isLoading - boolean to indicate loading state: banner container will
 * render but inner content is hidden in this state
 * @param title - The string title that will be centered in the banner
 * @param actions - The ReactNode actions that will be rendered on the right
 * side of the banner. These will be rendered in a flex container with a gap of
 * theme.space['300']. The actions container will be rendered in a
 * column-reverse flex when the space gets constrained. Due to limited space,
 * the intended usage is for 2 action nodes that should fit within the max
 * banner height of 62px.
 * @param ref - forwardRef is utilized to pass the ref to the outer banner container
 */
export const Banner = /*#__PURE__*/forwardRef(({
  id,
  isLoading,
  title,
  actions
}, ref) => {
  const theme = useTheme();
  const isTrellis = isTrellisTheme(theme);
  return /*#__PURE__*/_jsx(BannerContainer, {
    id: id,
    ref: ref,
    isTrellis: isTrellis,
    hasActions: !!actions,
    "data-test-id": id,
    children: !isLoading && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(PrimaryTitleContainer, {
        children: /*#__PURE__*/_jsx(PrimaryTitle, {
          children: title
        })
      }), /*#__PURE__*/_jsx(CTAContainer, {
        children: actions
      })]
    })
  });
});